//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  IAddTrackedSourceRequest,
  IGetAllTrackedSourcesResponse,
  IGetTrackedSourcesResponse,
  IGetTrackedUsersRequest,
  IGetTrackedUsersResponse,
  IGetTrackedUsersResponseData,
} from '@apis/tracked-sources/trackedSourceApiModels';
import TrackedSourceType from '@models/TrackedSourceType';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch, sessionFetch } from '@utils/sessionFetch';

const TRACKED_SOURCES_BASE_URL = '/api/tracked-sources';
const TRACKED_USERS_BASE_URL = '/api/v1/tracked-sources/users';

/**
 * Gets all tracked sources for the current client
 * /api/tracked-sources/
 *
 * @returns An object containing lists of all tracked sources as a promise
 */
export async function getAllTrackedSources(
  init?: RequestInit,
): Promise<IGetAllTrackedSourcesResponse> {
  const res = await sessionFetch(TRACKED_SOURCES_BASE_URL, {
    ...(init ?? {}),
    method: 'GET',
  });

  return res.json();
}

/**
 * Gets all tracked sources for the current client with a given sourceType
 * /api/tracked-sources/:sourceType
 *
 * @param sourceType
 *
 * @returns A list of tracked sources as a promise
 */
export async function getTrackedSources(
  sourceType: TrackedSourceType,
): Promise<IGetTrackedSourcesResponse> {
  const res = await sessionFetch(`${TRACKED_SOURCES_BASE_URL}/${sourceType}`, {
    method: 'GET',
  });

  return res.json();
}

/**
 * Gets all tracked users for the current client
 * /api/tracked-sources/users
 *
 * @returns A list of tracked users as a promise
 */
export async function getAllTrackedUsers(
  request: IGetTrackedUsersRequest,
  init?: RequestInit,
): Promise<IGetTrackedUsersResponseData> {
  const params = makeParams({
    sortBy: request.sortBy,
    sortOrder: request.sortOrder,
  });

  const res = await serializedGatewayFetch(
    'web_gateway',
    `${TRACKED_USERS_BASE_URL}?${params}`,
    { ...init, method: 'GET' },
  );
  const getUsersResponse = await res.json<IGetTrackedUsersResponse>();
  return getUsersResponse.data;
}

/**
 * Gets all tracked hashtags for the current client
 * /api/tracked-sources/hashtag
 *
 * @param sourceType
 *
 * @returns A list of tracked sources as a promise
 */
export async function getAllTrackedHashtags(
  init?: RequestInit,
): Promise<IGetTrackedSourcesResponse> {
  const res = await sessionFetch(`${TRACKED_SOURCES_BASE_URL}/hashtag`, {
    ...init,
    method: 'GET',
  });

  return res.json();
}

/**
 * Adds a tracked source for the current client
 * /api/tracked-sources
 *
 * @returns An empty promise
 */
export async function addTrackedSource(
  req: IAddTrackedSourceRequest,
): Promise<void> {
  await sessionFetch(TRACKED_SOURCES_BASE_URL, {
    method: 'POST',
    body: JSON.stringify({
      network: 'Instagram',
      ...req,
    }),
  });
}

// TODO: can remove once DEV-60542 is done
/**
 * Removes a tracked source for the current client
 * /api/tracked-sources/delete
 *
 * @param sourceId
 * @param sourceType
 *
 * @returns An empty promise
 */
export async function removeTrackedSource(
  sourceId: string,
  sourceType: TrackedSourceType,
): Promise<void> {
  await sessionFetch(`${TRACKED_SOURCES_BASE_URL}/delete`, {
    method: 'POST',
    body: JSON.stringify({
      network: 'Instagram',
      sourceId,
      sourceType,
    }),
  });
}
